import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { X, ChevronLeft, ChevronRight, Download, Copy, Send } from "lucide-react";
import { useState } from "react";

interface SharePNLScreenProps {
  onClose: () => void;
}

interface PNLCardData {
  pair: string;
  direction: "Long" | "Short";
  leverage: string;
  pnlPercent: string;
  pnlAmount?: string;
  entryPrice: string;
  markPrice: string;
  date: string;
  referralCode: string;
  theme: "light" | "dark";
  isProfit: boolean;
}

const MOCK_PNL_CARDS: PNLCardData[] = [
  {
    pair: "BTCUSDT Perpetual",
    direction: "Long",
    leverage: "120x",
    pnlPercent: "+34.34%",
    pnlAmount: "+$3443.34",
    entryPrice: "$40,343",
    markPrice: "$100,343",
    date: "2025-10-2 11:23:34",
    referralCode: "eregdDW32",
    theme: "light",
    isProfit: true,
  },
  {
    pair: "BTCUSDT Perpetual",
    direction: "Long",
    leverage: "120x",
    pnlPercent: "+34.34%",
    entryPrice: "$40,343",
    markPrice: "$100,343",
    date: "2025-10-2 11:23:34",
    referralCode: "eregdDW32",
    theme: "light",
    isProfit: true,
  },
  {
    pair: "BTCUSDT Perpetual",
    direction: "Short",
    leverage: "120x",
    pnlPercent: "-34.34%",
    entryPrice: "$40,343",
    markPrice: "$100,343",
    date: "2025-10-2 11:23:34",
    referralCode: "eregdDW32",
    theme: "light",
    isProfit: false,
  },
  {
    pair: "BTCUSDT Perpetual",
    direction: "Long",
    leverage: "120x",
    pnlPercent: "+34.34%",
    entryPrice: "$40,343",
    markPrice: "$100,343",
    date: "2025-10-2 11:23:34",
    referralCode: "eregdDW32",
    theme: "dark",
    isProfit: true,
  },
];

function PNLCard({ card }: { card: PNLCardData }) {
  useLingui();
  const isDark = card.theme === "dark";
  const bgClass = isDark ? "bg-[#1a1a1a]" : "bg-[#f5f5f5]";
  const textClass = isDark ? "text-foreground" : "text-[#1a1a1a]";
  const mutedText = isDark ? "text-[#888888]" : "text-[#666666]";
  const pnlColor = card.isProfit ? "text-[#00ec97]" : "text-[#ff4444]";
  const badgeBg = card.isProfit ? "bg-[#00ec97]/20 text-[#00ec97]" : "bg-[#ff4444]/20 text-[#ff4444]";

  return (
    <div className={`rounded-2xl relative w-full overflow-hidden ${bgClass}`}>
      <div
        className="pointer-events-none absolute right-0 top-0 h-full w-2/3 opacity-40"
        style={{
          background: isDark
            ? "linear-gradient(135deg, transparent 30%, rgba(0,236,151,0.15) 60%, rgba(0,236,151,0.3) 100%)"
            : "linear-gradient(135deg, transparent 20%, rgba(0,236,151,0.2) 50%, rgba(51,255,182,0.4) 100%)",
        }}
      />

      <div className="relative p-5 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NectarliquidSmallLogo />
            <span className={`text-sm font-semibold ${textClass}`}>Nectarliquid</span>
          </div>
          <span className={`text-xs md:text-sm ${mutedText} font-mono`}>{card.date}</span>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className={`text-lg md:text-xl font-bold ${textClass}`}>{card.pair}</h3>
            <span className={`rounded-md text-xs md:text-sm mt-2 inline-block px-3 py-1 font-semibold ${badgeBg}`}>
              {card.direction === "Long" ? t`Long` : t`Short`} {card.leverage}
            </span>
            <p className={`text-3xl md:text-4xl mt-3 font-bold ${pnlColor}`}>{card.pnlPercent}</p>
            {card.pnlAmount && <p className={`text-lg md:text-xl mt-1 font-semibold ${pnlColor}`}>{card.pnlAmount}</p>}
            <div className={`text-sm mt-4 flex flex-col gap-1 ${mutedText}`}>
              <span>
                <Trans>Entry price -</Trans>{" "}
                <strong className={textClass}>{card.entryPrice}</strong>
              </span>
              <span>
                <Trans>Mark price -</Trans>{" "}
                <strong className={textClass}>{card.markPrice}</strong>
              </span>
            </div>
          </div>

          <div className="rounded-xl flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden md:h-32 md:w-32">
            <div
              className="rounded-xl h-full w-full"
              style={{
                background: isDark
                  ? "radial-gradient(circle at center, rgba(0,236,151,0.15), transparent 70%)"
                  : "radial-gradient(circle at center, rgba(0,236,151,0.1), transparent 70%)",
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-end justify-between pt-3">
          <span className={`text-xs md:text-sm ${mutedText}`}>app.nectarliquid.com</span>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className={`text-xs ${mutedText} block`}><Trans>Referral code</Trans></span>
              <span className={`text-sm md:text-base font-mono font-bold ${textClass}`}>{card.referralCode}</span>
            </div>
            <div className="bg-foreground rounded-md flex h-12 w-12 items-center justify-center md:h-14 md:w-14">
              <svg viewBox="0 0 24 24" className="h-8 w-8 md:h-10 md:w-10" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1" fill={isDark ? "#1a1a1a" : "#f5f5f5"} />
                <rect x="14" y="3" width="7" height="7" rx="1" fill={isDark ? "#1a1a1a" : "#f5f5f5"} />
                <rect x="3" y="14" width="7" height="7" rx="1" fill={isDark ? "#1a1a1a" : "#f5f5f5"} />
                <rect x="14" y="14" width="4" height="4" rx="0.5" fill={isDark ? "#1a1a1a" : "#f5f5f5"} />
                <rect x="19" y="17" width="2" height="4" rx="0.5" fill={isDark ? "#1a1a1a" : "#f5f5f5"} />
                <rect x="5" y="5" width="3" height="3" rx="0.5" fill={isDark ? "#ffffff" : "#1a1a1a"} />
                <rect x="16" y="5" width="3" height="3" rx="0.5" fill={isDark ? "#ffffff" : "#1a1a1a"} />
                <rect x="5" y="16" width="3" height="3" rx="0.5" fill={isDark ? "#ffffff" : "#1a1a1a"} />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NectarliquidSmallLogo() {
  return (
    <div className="bg-primary flex h-5 w-5 items-center justify-center rounded-full md:h-6 md:w-6">
      <svg width="12" height="12" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M25 75 C25 75, 30 25, 40 35 C50 45, 45 70, 55 55 C65 40, 60 25, 75 25"
          stroke="#0c0c0c"
          strokeWidth="12"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function SharePNLScreen({ onClose }: SharePNLScreenProps) {
  useLingui();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPnlAmount, setShowPnlAmount] = useState(true);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? MOCK_PNL_CARDS.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === MOCK_PNL_CARDS.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="bg-background flex min-h-screen flex-col px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto flex w-full max-w-lg items-center justify-between">
        <h1 className="text-2xl md:text-3xl text-foreground mx-auto font-bold"><Trans>Share PnL</Trans></h1>
        <button
          onClick={onClose}
          className="bg-muted/60 text-foreground hover:bg-muted absolute right-4 flex h-10 w-10 items-center justify-center rounded-full transition-colors md:right-8 md:h-12 md:w-12"
          aria-label={t`Close`}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="mx-auto mt-6 flex w-full max-w-lg flex-1 flex-col items-center md:mt-10">
        <div className="relative w-full">
          <button
            onClick={handlePrev}
            className="bg-primary text-primary-foreground absolute left-0 top-1/2 z-10 flex h-8 w-8 -translate-x-3 -translate-y-1/2 items-center justify-center rounded-full transition-all hover:brightness-110 md:h-10 md:w-10 md:-translate-x-5"
            aria-label={t`Previous card`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleNext}
            className="bg-primary text-primary-foreground absolute right-0 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 translate-x-3 items-center justify-center rounded-full transition-all hover:brightness-110 md:h-10 md:w-10 md:translate-x-5"
            aria-label={t`Next card`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="px-2">
            <PNLCard card={MOCK_PNL_CARDS[currentIndex]} />
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          {MOCK_PNL_CARDS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-1 rounded-full transition-all ${i === currentIndex ? "bg-primary w-6" : "bg-muted w-4"}`}
              aria-label={t`Go to card ${i + 1}`}
            />
          ))}
        </div>

        <div className="mt-4 flex w-full items-center justify-end">
          <label className="flex cursor-pointer items-center gap-2">
            <div
              onClick={() => setShowPnlAmount(!showPnlAmount)}
              className={`rounded flex h-5 w-5 items-center justify-center border-2 transition-colors ${
                showPnlAmount ? "border-primary bg-primary/20" : "border-muted-foreground"
              }`}
            >
              {showPnlAmount && (
                <svg
                  viewBox="0 0 16 16"
                  className="text-primary h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3,8 7,12 13,4" />
                </svg>
              )}
            </div>
            <span className="text-foreground text-sm font-medium"><Trans>PnL amount</Trans></span>
          </label>
        </div>

        <div className="mt-8 flex w-full items-center justify-center gap-6 md:mt-12 md:gap-10">
          <button className="text-muted-foreground hover:text-foreground flex flex-col items-center gap-2 transition-colors">
            <Download className="h-5 w-5" />
            <span className="text-xs md:text-sm"><Trans>Download</Trans></span>
          </button>
          <button className="text-muted-foreground hover:text-foreground flex flex-col items-center gap-2 transition-colors">
            <Copy className="h-5 w-5" />
            <span className="text-xs md:text-sm"><Trans>Copy link</Trans></span>
          </button>
          <button className="text-muted-foreground hover:text-foreground flex flex-col items-center gap-2 transition-colors">
            <Send className="h-5 w-5" />
            <span className="text-xs md:text-sm">Telegram</span>
          </button>
          <button className="text-muted-foreground hover:text-foreground flex flex-col items-center gap-2 transition-colors">
            <XIcon />
            <span className="text-xs md:text-sm">X</span>
          </button>
        </div>
      </div>
    </div>
  );
}
